import { useState, useEffect, useRef, isValidElement } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useShippingRates } from "../utils/shipping";
import useQuoteRegion, { useQuoteLimits } from "../hooks/useQuoteRegion";
import useHappytalk from "../hooks/useHappytalk";
import "./PrintingFAQ.css";

/* 답변 JSX 를 검색용 글자로 펼친다 — 답변을 따로 두 벌 적지 않으려고 (2026-10-16 고객지원 개편) */
function nodeText(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(nodeText).join(" ");
  if (isValidElement(node)) return nodeText(node.props.children);
  return "";
}

/* 많이 찾는 질문 칩 → 실제 항목 id */

export default function PrintingFAQ() {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const shipping = useShippingRates();
  const { minOrderPrice } = useQuoteLimits();
  /* 3D프린팅 안내는 국내 전용 서비스(출력·견적·배송) 설명이라 접수 페이지와 같은 기준으로 가린다.
     쿠키커터 안내는 제품 자체 설명이라 해외에서도 그대로 보인다 (2026-09-09 김현비).
     판정은 서버 IP — 확인 중(null)에는 흔들지 않으려고 종전대로 보여준다. */
  const printingAllowed = useQuoteRegion() !== false;
  const shipVars = {
    base: shipping.base.toLocaleString(),
    jeju: shipping.jeju.toLocaleString(),
    island: shipping.island.toLocaleString(),
    jejuTotal: (shipping.base + shipping.jeju).toLocaleString(),
    islandTotal: (shipping.base + shipping.island).toLocaleString(),
    intl: shipping.intl.toLocaleString(),
  };
  const params = new URLSearchParams(location.search);
  const rawTab = params.get("tab");
  const tabParam = rawTab === "printing" || rawTab === "cookie" ? rawTab : "all";
  const [activeTab, setActiveTab] = useState(tabParam);
  const [openId, setOpenId] = useState(null);
  const [query, setQuery] = useState("");
  const [scrollTo, setScrollTo] = useState(null);
  const openContact = useHappytalk();
  const itemRefs = useRef({});

  useEffect(() => {
    setActiveTab(tabParam);
    setOpenId(null);
  }, [tabParam]);

  useEffect(() => {
    if (!scrollTo) return;
    const el = itemRefs.current[scrollTo];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    setScrollTo(null);
  }, [scrollTo]);

  const PRINTING_FAQ = [
    {
      id: "p-order",
      title: t("printingFaq.printing.qOrderTitle"),
      content: (
        <>
          <ol>
            <li>{t("order.guide.step1")}</li>
            <li>{t("order.guide.step2")}</li>
            <li>{t("order.guide.step3")}</li>
            <li>{t("order.guide.step4")}</li>
          </ol>
          <p className="faq-note">
            {minOrderPrice != null && t("order.guide.minOrder", { price: Number(minOrderPrice).toLocaleString() })}
            {" "}
            {t("order.guide.shipping", { ship: shipping.base.toLocaleString() })}
          </p>
        </>
      ),
    },
    {
      id: "p-quote",
      title: t("printingFaq.printing.q1Title"),
      content: (
        <>
          <p>{t("printingFaq.printing.q1Intro")}</p>
          <h4>{t("printingFaq.printing.q1OptionListTitle")}</h4>
          <ul>
            <li>{t("printingFaq.printing.q1Option1")}</li>
            <li>{t("printingFaq.printing.q1Option2")}</li>
            <li>{t("printingFaq.printing.q1Option3")}</li>
            <li>{t("printingFaq.printing.q1Option4")}</li>
            <li>{t("printingFaq.printing.q1Option5")}</li>
            <li>{t("printingFaq.printing.q1Option6")}</li>
            <li>{t("printingFaq.printing.q1Option7")}</li>
          </ul>
          <p className="faq-note">{t("printingFaq.printing.q1Note")}</p>
        </>
      ),
    },
    {
      id: "p-lead",
      title: t("printingFaq.printing.q2Title"),
      content: (
        <>
          <p>
            {t("printingFaq.printing.q2p1Pre")}
            <b>{t("printingFaq.printing.q2p1Bold")}</b>
            {t("printingFaq.printing.q2p1Post")}
          </p>
          <p>
            {t("printingFaq.printing.q2p2Pre")}
            <b>{t("printingFaq.printing.q2p2Bold")}</b>
            {t("printingFaq.printing.q2p2Post")}
          </p>
          {false && <p>
            {t("printingFaq.printing.q2p3Pre")}
            <b>{t("printingFaq.printing.q2p3Bold")}</b>
            {t("printingFaq.printing.q2p3Post")}
          </p>}
          <p className="faq-note">{t("printingFaq.printing.q2Note")}</p>
        </>
      ),
    },
    {
      id: "p-refund",
      title: t("printingFaq.printing.q3Title"),
      content: (
        <ol>
          <li>{t("printingFaq.printing.q3Item1")}</li>
          <li>{t("printingFaq.printing.q3Item2")}</li>
          <li>{t("printingFaq.printing.q3Item3")}</li>
          <li>{t("printingFaq.printing.q3Item4")}</li>
        </ol>
      ),
    },
    {
      id: "p-format",
      title: t("printingFaq.printing.q4Title"),
      content: (
        <>
          <p>
            {t("printingFaq.printing.q4p1Pre")}
            <b>{t("printingFaq.printing.q4p1Formats")}</b>
          </p>
          <p className="faq-note">{t("printingFaq.printing.q4Note")}</p>
        </>
      ),
    },
    {
      id: "p-ship",
      title: t("printingFaq.printing.q5Title"),
      content: (
        <>
          <p><b>📦 {t("printingFaq.printing.q5OriginTitle")}</b></p>
          <p>{t("printingFaq.printing.q5Origin")}</p>

          <h4>{t("printingFaq.printing.q5DomesticTitle")}</h4>
          <ul>
            <li>{t("printingFaq.printing.q5Domestic1", shipVars)}</li>
            <li>{t("printingFaq.printing.q5Domestic2", shipVars)}</li>
            <li>{t("printingFaq.printing.q5Domestic3", shipVars)}</li>
          </ul>

          {false && <>
          <h4>{t("printingFaq.printing.q5InternationalTitle")}</h4>
          <ul>
            <li>{t("printingFaq.printing.q5International1", shipVars)}</li>
            <li>{t("printingFaq.printing.q5International2")}</li>
            <li>{t("printingFaq.printing.q5International3")}</li>
          </ul>

          <h4>🔍 {t("printingFaq.printing.q5TrackingTitle")}</h4>
          <p>{t("printingFaq.printing.q5Tracking")}</p>
          <p>{t("printingFaq.printing.q5TrackingIntl")}</p>

          <h4>⚠️ {t("printingFaq.printing.q5CustomsTitle")}</h4>
          <ul>
            <li>{t("printingFaq.printing.q5Customs1")}</li>
            <li><b>{t("printingFaq.printing.q5Customs2")}</b></li>
            <li>{t("printingFaq.printing.q5Customs3")}</li>
          </ul>
          </>}
        </>
      ),
    },
  ];

  const COOKIE_FAQ = [
    {
      id: "c-caution",
      title: t("printingFaq.cookie.q1Title"),
      content: (
        <ol>
          <li>{t("printingFaq.cookie.q1Item1")}</li>
          <li>{t("printingFaq.cookie.q1Item2")}</li>
          <li>{t("printingFaq.cookie.q1Item3")}</li>
          <li>{t("printingFaq.cookie.q1Item4")}</li>
          <li>
            {t("printingFaq.cookie.q1Item5")}
            <img
              src="/printing-texture.png"
              alt={t("printingFaq.cookie.q1Item5Alt")}
              className="faq-img"
            />
            <p className="faq-sub">{t("printingFaq.cookie.q1Item5Sub")}</p>
          </li>
        </ol>
      ),
    },
    {
      id: "c-wash",
      title: t("printingFaq.cookie.q2Title"),
      content: (
        <ol>
          <li>
            {t("printingFaq.cookie.q2Item1")}
            <p className="faq-sub">{t("printingFaq.cookie.q2Item1Sub")}</p>
          </li>
          <li>{t("printingFaq.cookie.q2Item2")}</li>
          <li>{t("printingFaq.cookie.q2Item3")}</li>
        </ol>
      ),
    },
    {
      id: "c-tips",
      title: t("printingFaq.cookie.q3Title"),
      content: (
        <>
          <p>
            {t("printingFaq.cookie.q3p1Pre")}
            <b>{t("printingFaq.cookie.q3p1Bold")}</b>
            {t("printingFaq.cookie.q3p1Post")}
          </p>
          <p>
            {t("printingFaq.cookie.q3p2Pre")}
            <b>{t("printingFaq.cookie.q3p2Bold")}</b>
            {t("printingFaq.cookie.q3p2Post")}
          </p>
        </>
      ),
    },
    {
      id: "c-buy",
      title: t("printingFaq.cookie.q4Title"),
      content: (
        <ul>
          <li>
            <b>{t("printingFaq.cookie.q4Item1Bold")}</b>
            {t("printingFaq.cookie.q4Item1Text")}
          </li>
          <li>
            <b>{t("printingFaq.cookie.q4Item2Bold")}</b>
            {t("printingFaq.cookie.q4Item2Text")}
          </li>
        </ul>
      ),
    },
  ];

  const ALL_ITEMS = [
    ...COOKIE_FAQ.map((x) => ({ ...x, cat: "cookie" })),
    ...(printingAllowed ? PRINTING_FAQ.map((x) => ({ ...x, cat: "printing" })) : []),
  ];
  const TABS = [
    { key: "all", label: t("printingFaq.tabAll") },
    { key: "cookie", label: t("printingFaq.catCookie") },
    ...(printingAllowed ? [{ key: "printing", label: t("printingFaq.catPrinting") }] : []),
  ];
  // 없는 탭(해외에서 ?tab=printing)으로 들어오면 전체로 보여준다
  const tab = TABS.some((x) => x.key === activeTab) ? activeTab : "all";
  const q = query.trim().toLowerCase();
  const visible = ALL_ITEMS.filter((it) => {
    if (tab !== "all" && it.cat !== tab) return false;
    if (!q) return true;
    return `${it.title} ${nodeText(it.content)}`.toLowerCase().includes(q);
  });


  return (
    <div className="cs-faq">
      <div className="cs-faq-wrap">
        <div className="cs-faq-hero">
          <h1>{t("printingFaq.hubTitle")}</h1>
          <p>{t("printingFaq.hubSub")}</p>
          <label className="cs-faq-search">
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M21 21l-5-5" /></svg>
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("printingFaq.searchPlaceholder")}
              aria-label={t("printingFaq.searchPlaceholder")}
            />
            {query && (
              <button type="button" className="cs-faq-clear" onClick={() => setQuery("")} aria-label={t("printingFaq.clearSearch")}>×</button>
            )}
          </label>
          <button type="button" className="cs-faq-glink" onClick={() => history.push("/guide")}>
            📖 {t("printingFaq.guideLinkPre")}<b>{t("printingFaq.guideLinkBold")}</b>{t("printingFaq.guideLinkPost")} →
          </button>
        </div>

        <h2 className="cs-faq-h2">{t("printingFaq.faqHeading")}</h2>
        {TABS.length > 2 && (
          <div className="cs-faq-tabs">
            {TABS.map((x) => (
              <button
                type="button"
                key={x.key}
                className={tab === x.key ? "on" : ""}
                onClick={() => { setActiveTab(x.key); setOpenId(null); }}
              >
                {x.label}
              </button>
            ))}
          </div>
        )}

        {visible.length > 0 ? (
          <div className="cs-faq-acc">
            {visible.map((item) => {
              const open = openId === item.id;
              return (
                <div
                  key={item.id}
                  ref={(el) => { itemRefs.current[item.id] = el; }}
                  className={`cs-faq-it ${open ? "open" : ""}`}
                >
                  <button type="button" className="cs-faq-q" onClick={() => setOpenId(openId === item.id ? null : item.id)} aria-expanded={open}>
                    <span className={`cs-faq-tg ${item.cat === "printing" ? "is-printing" : "is-cookie"}`}>{item.cat === "printing" ? t("printingFaq.catPrinting") : t("printingFaq.catCookie")}</span>
                    <span className="cs-faq-qt">{item.title}</span>
                    <span className="cs-faq-pl" aria-hidden="true">{open ? "−" : "+"}</span>
                  </button>
                  {open && <div className="faq-answer cs-faq-ans">{item.content}</div>}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="cs-faq-empty">
            <b>{t("printingFaq.emptyTitle")}</b>
            <p>{t("printingFaq.emptyDesc")}</p>
            <button type="button" className="cs-faq-btn car" onClick={openContact}>{t("printingFaq.bandContact")}</button>
          </div>
        )}

        <div className="cs-faq-band">
          <div>
            <h3>{t("printingFaq.bandTitle")}</h3>
            <p>{t("printingFaq.bandSub")}</p>
          </div>
          <div className="cs-faq-bt">
            <button type="button" className="cs-faq-btn car" onClick={openContact}>{t("printingFaq.bandContact")}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
